import Image from "next/image"
import { useState } from "react"
import { HiArrowLongLeft, HiArrowLongRight } from "react-icons/hi2"
import PrimaryButton from "../../components/buttons/PrimaryButton"
import Markdown from "../../components/content/Markdown"

import HowItWorks1 from "../../assets/images/how-it-works/how-it-works1.png"
import HowItWorks2 from "../../assets/images/how-it-works/how-it-works2.png"
import HowItWorks3 from "../../assets/images/how-it-works/how-it-works3.png"
import HowItWorks4 from "../../assets/images/how-it-works/how-it-works4.png"
import * as config from "../../util/config"

export default function HowItWorks() {
  const appHost = config.getAppHost()
  const host = config.getWebsiteHost()
  const steps = [
    {
      image: HowItWorks1,
      title: "Step #1: List your transport for free",
      description: `You’ll describe your pet or the item you’re transporting, where it’s located and where it’s going. Just [fill out this form](${appHost}/shipments/new/animals), which takes about two minutes.
      \nDon’t limit yourself to pick-up and drop-off information — include every detail that you think might be relevant. Does your kitty need cuddles every three hours? Maybe your boat’s coating is especially sensitive to humidity? The more info you provide, the better we’ll be able to match you with the perfect transporter.`,
    },
    {
      image: HowItWorks2,
      title: "Step #2: Select your favorite driver",
      description: `As soon as you list your transport, we send it to a fleet of expert drivers that fit your criteria. Within minutes, you’ll receive messages and quotes for free.
      \nDrivers on CitizenShipper are always pre-screened, ID-verified and background-checked. Read more about our screening process [here](https://help.citizenshipper.com/hc/en-us/articles/203050235-What-does-the-driver-background-screening-report-on-). We also cover pet transports with a $1,000 Pet Protection Plan and access to 24/7 televet care.
      \nDrivers on CitizenShipper love pets as much as you do and are eager to spend time with your best friend!
      \nEach transporter’s credentials and experience are listed in their [CitizenShipper profile](https://app.citizenshipper.com/login?redirectTo=/profile/pjs-transport-service/109623). We strongly encourage you to consider this information when choosing which of them to hire. Check out their average rating on a five-star scale, or go through the reviews left by their previous customers. You can also compare their achievements, completed shipment numbers, and cancelation rates. Ultimately, the decision is yours to make. We notify you by email or text message of each bid, leaving you the freedom to choose the transporter that best meets your requirements.`,
    },
    {
      image: HowItWorks3,
      title: "Step #3: Stay in touch with your driver",
      description: `Before and after accepting a bid, you’ll chat with your driver at your convenience. Feel free to discuss any questions you have or details that are unclear with full transparency!
      \nWant a better offer or different terms of payment? The transporters are open to negotiations. How about regular check-ins while your shipment is en route to provide peace of mind? Not a problem. Last-minute changes or clarifications? Transporters will do their best to accommodate you.
      \nMost customers, especially in pet transport, appreciate drivers that communicate in an open and forthright manner. You’ll find this factor reflected in ratings and review scores. For our own part, we strive to maintain the same standards; feel free to [contact us](https://help.citizenshipper.com/hc/en-us/requests/new) at any time.`,
    },
    {
      image: HowItWorks4,
      title: "Step #4: Rate and review your experience ",
      description: `We’re focused on building a vibrant community. Shipping customers rely on each other by sharing their experiences and promoting those drivers who’ve earned their trust. You’ll find [thousands of reviews](${host}/reviews) talking about what works and what doesn’t. They’re all 100% genuine and traceable to the original transport.
      \nOur door-to-door delivery process encourages interaction between the transporter and the customer. Once the shipment arrives, you’ll be able to hear how the trip went — maybe even share photos or videos. In return, we hope you’ll take the time to rate and review your driver. By letting us know how well they have performed, you contribute to the platform’s continual improvement. The best drivers keep rising to the top, and new customers get a better idea of what to expect.`,
    },
  ]

  const [step, setStep] = useState(0)

  const prevStep = () => {
    setStep(Math.max(0, step - 1))
  }

  const nextStep = () => {
    setStep(Math.min(steps.length - 1, step + 1))
  }

  return (
    <section className="w-full page-padding">
      <h3 className="mb-4 text-4xl text-center text-blue7 font-montserrat">
        How CitizenShipper Works
      </h3>
      <p className="max-w-prose mx-auto text-gray8 text-xl text-center">
        No matter what you’re transporting, CitizenShipper has got you covered.
        We connect you with professional drivers eager and able to meet your
        requests.
      </p>
      <p className="mb-10 md:mb-16 text-xl text-center font-semibold">
        Here's how.
      </p>
      {steps.map(({ image, title, description }, i) => (
        <figure
          key={`HowItWorks/figure/${i}`}
          className={
            "relative md:block flex lg:flex flex-col lg:flex-row gap-y-4" +
            " " +
            (i === step ? "" : "hidden md:hidden lg:hidden")
          }
        >
          <Image
            className="md:w-1/2 md:float-left lg:pr-24 object-contain"
            src={image}
            alt={title}
            height={538}
            width={655}
          />

          <figcaption className="lg:w-1/2 ml-auto space-y-6">
            <h4 className="font-montserrat font-semibold text-2xl text-gray8">
              {title}
            </h4>
            <Markdown className="prose-p:text-gray7 prose-p:text-base">
              {description}
            </Markdown>
            <div className="flex space-x-6">
              <button
                onClick={prevStep}
                className="disabled:opacity-50"
                disabled={step === 0}
              >
                <HiArrowLongLeft className="h-10 w-10 stroke-0" />
              </button>
              <button
                onClick={nextStep}
                className="disabled:opacity-50"
                disabled={step === steps.length - 1}
              >
                <HiArrowLongRight className="h-10 w-10 stroke-0" />
              </button>
            </div>
            <PrimaryButton
              className="lg:px-20"
              onClick={() => window.location.assign(`${appHost}/shipments/new`)}
            >
              Transport Your Items Now
            </PrimaryButton>
          </figcaption>
        </figure>
      ))}
    </section>
  )
}
